import { useState, useEffect } from 'react'
import { getLeadDetail } from 'apps/leads/api/lead'
import { getIssueDetail } from 'apps/service/api/issue'

// Repair path for links created before every create path stamped the full lead/issue
// record into target_object_data (see eventLinks.js resolveLeadLink / resolveIssueLink):
// an old link can have a uid + type but an empty target_object_data. Two consumers —
//  - task/event LIST rows read the name through useLinkLabel / LinkLabel below, which
//    fetches the detail (shared, cached, rate-limited) when the stored data is empty;
//  - the detail view's LinkedItemsList already fetches each linked record fresh, and
//    hands it back through its onBackfill so the caller can write it into the link.

const LEAD_TYPE = 'lead/Lead'
const ISSUE_TYPE = 'project/Issue'

export const hasLinkData = (link) => {
  const data = link?.target_object_data
  return Boolean(data) && typeof data === 'object' && Object.keys(data).length > 0
}

// A link whose target we know how to fetch.
export const isResolvableLink = (link) => (
  Boolean(link?.target_object_uid) && (link?.target_object_type === LEAD_TYPE || link?.target_object_type === ISSUE_TYPE)
)

// The link has what it takes to resolve but is missing its record data.
export const needsLinkData = (link) => isResolvableLink(link) && !hasLinkData(link)

export const fetchLinkedRecord = (link) => (
  link?.target_object_type === LEAD_TYPE
    ? getLeadDetail(link.target_object_uid)
    : getIssueDetail(link.target_object_uid)
)

// --- shared cache for list rows -------------------------------------------------------
// A task list can hold hundreds of rows, many pointing at the same lead/issue: one
// in-flight/settled request per record (keyed type:uid), at most MAX_CONCURRENT at once.
// Failed fetches are dropped from the cache so a later mount can retry.
const CACHE_TTL_MS = 5 * 60 * 1000
const MAX_CONCURRENT = 4
const cache = new Map()
const queue = []
let running = 0

const pump = () => {
  while (running < MAX_CONCURRENT && queue.length) {
    const job = queue.shift()
    running++
    job().finally(() => { running--; pump() })
  }
}

const enqueue = (fn) => new Promise((resolve) => {
  queue.push(() => fn().then(resolve, () => resolve(null)))
  pump()
})

const keyOf = (link) => `${link.target_object_type}:${link.target_object_uid}`

export const getLinkedRecordCached = (link) => {
  const key = keyOf(link)
  const hit = cache.get(key)
  if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.promise
  const promise = enqueue(async () => {
    const { ok, data } = await fetchLinkedRecord(link)
    return ok ? data : null
  })
  cache.set(key, { at: Date.now(), promise })
  promise.then((rec) => { if (!rec && cache.get(key)?.promise === promise) cache.delete(key) })
  return promise
}

const storedLabel = (link) => link?.target_object_data?.name || link?.title || `#${link?.target_object_uid}`

// The fetched record's name for a link that has no stored data — null while loading, when
// the link already carries data (nothing to resolve), or when the fetch fails. For rows
// that build their own fallback label (event rows fall back to the type name, not #uid):
//   useLinkedRecordName(link) || <their own stored-label chain>
export const useLinkedRecordName = (link) => {
  const needs = needsLinkData(link)
  const type = link?.target_object_type
  const uid = link?.target_object_uid
  const [name, setName] = useState(null)

  useEffect(() => {
    setName(null)
    if (!needs) return undefined
    let cancelled = false
    getLinkedRecordCached(link).then((rec) => { if (!cancelled && rec?.name) setName(rec.name) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needs, type, uid])

  return (needs && name) || null
}

// Display label for a link in a task list row: the stored name/title when there is data,
// else that same fallback immediately and then the fetched record's name once it resolves.
export const useLinkLabel = (link) => useLinkedRecordName(link) || storedLabel(link)

export function LinkLabel({ link }) {
  return <>{useLinkLabel(link)}</>
}
